import { useCallback, useEffect, useMemo, useState } from 'react';

import { imageVariantLink } from '@/common/media';
import type { CrewMember } from '@/components/Widget/CrewWidget';
import type { Person } from '@/store/persons/person.dto';
import { mapPeopleToCrewMembers } from '@/store/persons/person.mapper';
import { usePerson } from '@/store/persons/person.store';
import { useVessels } from '@/store/vessels/vessels.store';
import { UserRole } from '@/utils/roles';

const ROLE_RANK = new Map<UserRole | string, number>([
  [UserRole.OWNER, 0],
  [UserRole.CO_OWNER, 0.5],
  [UserRole.CAPTAIN, 1],
  [UserRole.ENGINEER, 2],
  [UserRole.CREW_MEMBER, 3],
  [UserRole.SERVICE_PROVIDER, 4],
  [UserRole.GUEST, 5],
]);

export type CrewSortBy = 'hierarchy' | 'frequency';

export type VesselOwner = {
  role: UserRole.OWNER | UserRole.CO_OWNER;
  member: CrewMember;
};

export const useCrewManagement = (sortBy: CrewSortBy = 'hierarchy', forVesselId?: string) => {
  const {
    people,
    currentUser,
    getPeople,
    getPeopleByService,
    assignRoleToPerson,
    deletePersonRoles,
  } = usePerson();
  const { vessel } = useVessels();
  const vesselId = forVesselId ?? vessel?.id ?? null;
  // The global people list is scoped to the active vessel (X-Service-Id), so a page showing
  // another vessel fetches that vessel's people separately instead of filtering the wrong list.
  const [scoped, setScoped] = useState<{ id: string; people: Person[] } | null>(null);
  const isScoped = !!forVesselId && forVesselId !== vessel?.id;

  useEffect(() => {
    getPeople(sortBy === 'frequency' ? { order_by: 'frequency_communication' } : undefined);
  }, [getPeople, sortBy]);

  useEffect(() => {
    if (!isScoped || !forVesselId) return;

    let cancelled = false;
    void getPeopleByService(
      forVesselId,
      sortBy === 'frequency' ? { order_by: 'frequency_communication' } : undefined,
    ).then((result) => {
      if (!cancelled) setScoped({ id: forVesselId, people: result });
    });

    return () => {
      cancelled = true;
    };
  }, [isScoped, forVesselId, sortBy, getPeopleByService]);

  const sourcePeople = useMemo(() => {
    if (!isScoped) return people;
    return scoped?.id === forVesselId ? scoped.people : [];
  }, [isScoped, people, scoped, forVesselId]);

  const crewMembers = useMemo(() => {
    const mapped = mapPeopleToCrewMembers(sourcePeople, vesselId) ?? [];

    if (sortBy === 'frequency') return mapped;

    return [...mapped].sort((a, b) => {
      const aVesselRole = a.services?.find((r) => r.service_id === vesselId);
      const bVesselRole = b.services?.find((r) => r.service_id === vesselId);
      const aRoleRank = ROLE_RANK.get(aVesselRole?.role ?? '') ?? 99;
      const bRoleRank = ROLE_RANK.get(bVesselRole?.role ?? '') ?? 99;
      return aRoleRank - bRoleRank;
    });
  }, [sourcePeople, vesselId, sortBy]);

  const crewMemberIds = useMemo(() => crewMembers.map((c) => c.id), [crewMembers]);

  /**
   * The vessel's owner group — the owner first, then the co-owners in the order the API gave.
   * Kept apart from `crewMembers`, which leaves both out: owners are not crew.
   */
  const owners = useMemo<VesselOwner[]>(() => {
    if (!vesselId) return [];
    return sourcePeople
      .flatMap((person) => {
        const role = person.services?.find((r) => r.service_id === vesselId)?.role;
        if (role !== UserRole.OWNER && role !== UserRole.CO_OWNER) return [];
        return [
          {
            role: role as VesselOwner['role'],
            member: {
              id: person.id,
              firstName: person.person_info.first_name,
              lastName: person.person_info.last_name,
              // OwnershipSection draws it at 36 px.
              avatar: imageVariantLink(person.avatar, 36) ?? undefined,
              services: person.services ?? [],
            },
          },
        ];
      })
      .sort((a, b) => Number(b.role === UserRole.OWNER) - Number(a.role === UserRole.OWNER));
  }, [sourcePeople, vesselId]);

  // Filter out current user from available people
  const availablePeople = useMemo(() => {
    if (!Array.isArray(people)) return [];
    if (!currentUser) return people;
    return people.filter((person) => person.id !== currentUser.id);
  }, [people, currentUser]);

  const getPeopleParams = useMemo(
    () => (sortBy === 'frequency' ? { order_by: 'frequency_communication' } : undefined),
    [sortBy],
  );

  const addCrewMember = useCallback(
    async (personId: string) => {
      if (!vesselId) return;

      await assignRoleToPerson({
        person_id: personId,
        service_id: vesselId,
        service_type: 'vessel',
        role: UserRole.CREW_MEMBER,
      });

      await getPeople(getPeopleParams);
    },
    [vesselId, assignRoleToPerson, getPeople, getPeopleParams],
  );

  const deleteCrewMember = useCallback(
    async (personId: string) => {
      if (!vesselId) return;

      await deletePersonRoles({
        person_id: personId,
        service_id: vesselId,
      });

      await getPeople(getPeopleParams);
    },
    [vesselId, deletePersonRoles, getPeople, getPeopleParams],
  );

  /** Re-reads the people this hook shows, for the vessel it was asked about. */
  const refresh = useCallback(async () => {
    if (isScoped && forVesselId) {
      const result = await getPeopleByService(forVesselId, getPeopleParams);
      setScoped({ id: forVesselId, people: result });
      return;
    }
    await getPeople(getPeopleParams);
  }, [isScoped, forVesselId, getPeopleByService, getPeople, getPeopleParams]);

  return {
    crewMembers,
    crewMemberIds,
    owners,
    refresh,
    allPeople: availablePeople,
    vessel,
    addCrewMember,
    deleteCrewMember,
  };
};
